import { ChangeDetectionStrategy, Component, Injector, OnInit, computed, effect, inject, input, output, viewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ctrlContainerType, sectionContainerNumber } from 'src/app/shared/_config/ctrls/ctrls-constants';
import { textCassette, textContainer, textMiscellaneous, textTrailer, textUnitType } from 'src/app/shared/_config/i18n/i18n-interface';

import { ContainerTypeEnum } from 'src/app/shared/models/shared/shared-containertype.enum';
import { SharedFieldTypeEnum } from 'src/app/shared/models/shared/shared-field-type.enum';
import { SharedFormViewComponent } from 'src/app/pages/_templates/shared-form-view/shared-form-view.component';
import { SharedPageTypeEnum } from 'src/app/shared/navigation/page-type.enum';
import { SharedSectionI } from 'src/app/shared/models/shared/shared-section.interface';
import { SharedStorageKeyEnum } from 'src/app/shared/_config/storage/shared-storage-key-enum';
import { SpxStorage } from '@softpak/components/spx-storage';
import { SpxValuePair } from '@softpak/components/spx-inputs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { sharedDepotInfoReducer } from 'src/app/store/shared-depot-info';
import { spValRequired } from '../../validators';
import { startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { valuePairToValue } from '@softpak/components/spx-helpers';

export interface SharedSearchTypeChangeEvent {
  containerType: ContainerTypeEnum;
  isOnInitialization: boolean;
}

@Component({
  selector: 'shared-search-type',
  templateUrl: './shared-search-type.component.html',
  imports: [SharedFormViewComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedSearchTypeComponent implements OnInit {
  // ViewChild
  private readonly formView = viewChild<SharedFormViewComponent>('formView');

  // Injects
  private readonly injector = inject(Injector);
  private readonly appStore = inject(Store);
  private readonly translateService = inject(TranslateService);

  // Signals
  public readonly formGroup = input.required<FormGroup>();
  public readonly pageType = input.required<SharedPageTypeEnum>();
  public readonly resolvedPageType = input.required<SharedPageTypeEnum>();
  public readonly showRoroForComponent = input<boolean>(false);
  protected readonly containerTypeChange = output<SharedSearchTypeChangeEvent>();
  private readonly showRoRo = this.appStore.selectSignal(sharedDepotInfoReducer.default.selectShowRoRo);

  // Computeds
  private readonly containerTypeControl = computed(
    () => this.formGroup().get(ctrlContainerType) as FormControl | null
  );
  private readonly effectivePageType = computed<SharedPageTypeEnum>(
    () => this.resolvedPageType() ?? this.pageType()
  );
  private readonly shouldShowRoRo = computed(
    () => this.showRoroForComponent() && this.showRoRo()
  );
  private readonly suggestions = computed<SpxValuePair<string>[]>(
    () => this.getUnitTypeSuggestions()
  );
  private readonly includeCassette = computed(
    () => this.shouldShowRoRo() && this.effectivePageType() !== SharedPageTypeEnum.quaDischarge
  );

  readonly unitTypeSections: SharedSectionI[] = [
    {
      key: sectionContainerNumber,
      index: 1,
      show: () => true,
      showTitle: () => false,
      fields: [
        {
          label: () => textUnitType,
          key: ctrlContainerType,
          type: SharedFieldTypeEnum.radio,
          entity: undefined,
          defaultValue: { value: ContainerTypeEnum.container },
          validators: this.shouldShowRoRo() ? [spValRequired()] : [],
          readonly: () => false,
          suggestions: () => false,
          afterValueChange: undefined,
          foreignKeys: undefined,
          autofocus: () => false,
          show: () => this.shouldShowRoRo(),
          suggestionsFixed: () => this.suggestions(),
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.observeContainerTypeControlPresence();
    this.observeContainerTypeSelection();
    this.observeRoRoState();
  }

  triggerChangeDetection(): void {
    this.formView()?.triggerChangeDetection();
  }

  applyAutofocus(): void {
    this.formView()?.applyAutofocus();
  }

  private getUnitTypeSuggestions(): SpxValuePair<string>[] {
    const unitTypes = [
      this.createSuggestion(textContainer, ContainerTypeEnum.container),
      this.createSuggestion(textTrailer, ContainerTypeEnum.trailer),
      this.createSuggestion(textMiscellaneous, ContainerTypeEnum.miscellaneous),
    ];
    if (this.includeCassette()) {
      unitTypes.push(this.createSuggestion(textCassette, ContainerTypeEnum.cassette));
    }
    return unitTypes;
  }

  private createSuggestion(
    description: string,
    value: ContainerTypeEnum
  ): SpxValuePair<string> {
    return {
      description: this.translateService.instant(description),
      value,
    };
  }

  private getContainerTypeStorageKey(): string {
    return `${SharedStorageKeyEnum.selectedContainerType}_${this.effectivePageType()}`;
  }

  private getStoredContainerType(): ContainerTypeEnum | undefined {
    const pageTypeContainerType = SpxStorage.getSetting(this.getContainerTypeStorageKey()) as ContainerTypeEnum | undefined;
    if (pageTypeContainerType) {
      return pageTypeContainerType;
    }

    // Backward compatibility with older single-key storage.
    return SpxStorage.getSetting(SharedStorageKeyEnum.selectedContainerType) as ContainerTypeEnum | undefined;
  }

  private getInitialContainerTypeValue(): SpxValuePair<ContainerTypeEnum> {
    const storedContainerType = this.getStoredContainerType();
    const containerType = storedContainerType !== undefined
      ? (
        this.effectivePageType() !== SharedPageTypeEnum.infCnt &&
          storedContainerType === ContainerTypeEnum.cassette
          ? ContainerTypeEnum.container
          : storedContainerType
      )
      : ContainerTypeEnum.container;

    return { value: containerType };
  }

  private resolveContainerTypeFromControl(): ContainerTypeEnum | undefined {
    const control = this.containerTypeControl();
    if (!control) {
      return undefined;
    }
    return valuePairToValue(control.value) as ContainerTypeEnum | undefined;
  }

  private persistContainerTypePreference(
    containerType: ContainerTypeEnum,
    isOnInitialization: boolean
  ): void {
    if (!isOnInitialization) {
      SpxStorage.setSetting(this.getContainerTypeStorageKey(), containerType);
    }
  }

  private emitContainerTypeChange(
    containerType: ContainerTypeEnum,
    isOnInitialization: boolean
  ): void {
    this.containerTypeChange.emit({
      containerType,
      isOnInitialization,
    });
  }

  private observeContainerTypeSelection(): void {
    const control = this.ensureContainerTypeControl();
    const containerTypeValueSignal = toSignal(
      control.valueChanges.pipe(
        startWith(control.value)
      ),
      { initialValue: control.value, injector: this.injector }
    );

    let isInitialEmission = true;

    effect(() => {
      const valuePair = containerTypeValueSignal();
      const selectedType = valuePairToValue(valuePair) as ContainerTypeEnum | undefined;

      if (!selectedType) {
        control.setValue(this.getInitialContainerTypeValue());
        return;
      }

      this.persistContainerTypePreference(selectedType, isInitialEmission);
      this.emitContainerTypeChange(selectedType, isInitialEmission);
      isInitialEmission = false;
    }, { injector: this.injector });
  }

  private observeContainerTypeControlPresence(): void {
    effect(() => {
      this.formGroup();
      this.ensureContainerTypeControl();
    }, { injector: this.injector });
  }

  private ensureContainerTypeControl(): FormControl {
    const existingControl = this.containerTypeControl();
    if (existingControl) {
      return existingControl;
    }

    const createdControl = new FormControl<any>(this.getInitialContainerTypeValue());
    this.formGroup().addControl(ctrlContainerType, createdControl);
    return createdControl;
  }

  private resolveRoRoContainerType(): ContainerTypeEnum {
    const typeFromControl = this.resolveContainerTypeFromControl();
    const storedType = this.getStoredContainerType();
    return storedType ?? typeFromControl ?? ContainerTypeEnum.container;
  }

  private observeRoRoState(): void {
    let previousShouldShowRoRo: boolean | undefined;

    effect(() => {
      const shouldShowRoRo = this.shouldShowRoRo();
      if (previousShouldShowRoRo === shouldShowRoRo) {
        return;
      }

      previousShouldShowRoRo = shouldShowRoRo;
      if (!shouldShowRoRo) {
        // When RoRo functionality is disabled, force input mode back to container.
        this.emitContainerTypeChange(ContainerTypeEnum.container, true);
        return;
      }

      // Init/sync should never overwrite persisted preference.
      this.emitContainerTypeChange(this.resolveRoRoContainerType(), true);
    }, { injector: this.injector });
  }
}
